/**
 * DICOM US Region Calibration Module code tables.
 *
 * Spec: NEMA PS3.3 §C.8.5.5 (US Region Calibration Module).
 * https://dicom.nema.org/medical/dicom/current/output/chtml/part03/sect_C.8.5.5.html
 *
 * Mirror of `pacs-be/clinic/us_region_codes.py` — keep in sync.
 *
 * The backend serializes raw integer codes for RegionSpatialFormat,
 * RegionDataType, PhysicalUnitsXDirection, PhysicalUnitsYDirection and
 * RegionFlags (bitfield). The frontend decodes codes → labels and
 * derives region classification (spectral / tissue / color flow) from
 * `RegionDataType`. RegionFlags (0018,6016) is a separate tag that
 * carries priority/scaling/scrolling metadata — not classification.
 */

/** PS3.3 §C.8.5.5.1.1 — RegionSpatialFormat (0018,6012). */
export enum RegionSpatialFormat {
  NoneOrNotApplicable = 0,
  TwoD = 1,
  MMode = 2,
  Spectral = 3,
  Waveform = 4,
  Graphics = 5,
}

/** PS3.3 §C.8.5.5.1.2 — RegionDataType (0018,6014). Value 0x9 is reserved. */
export enum RegionDataType {
  NoneOrNotApplicable = 0,
  Tissue = 1,
  ColorFlow = 2,
  PWSpectralDoppler = 3,
  CWSpectralDoppler = 4,
  DopplerMeanTrace = 5,
  DopplerModeTrace = 6,
  DopplerMaxTrace = 7,
  VolumeTrace = 8,
  // 0x9 reserved — intentionally skipped.
  ECGTrace = 0xa,
  PulseTrace = 0xb,
  PhonocardiogramTrace = 0xc,
  GrayBar = 0xd,
  ColorBar = 0xe,
  IntegratedBackscatter = 0xf,
  AreaTrace = 0x10,
  DAreaDt = 0x11,
  OtherPhysiological = 0x12,
}

/** PS3.3 §C.8.5.5.1.15 — PhysicalUnitsX/YDirection (0018,6024 / 0018,6026). */
export enum PhysicalUnits {
  NoneOrNotApplicable = 0,
  Percent = 1,
  DB = 2,
  CM = 3,
  Seconds = 4,
  Hertz = 5,
  DBPerSecond = 6,
  CMPerSec = 7,
  CMSquared = 8,
  CMSquaredPerSec = 9,
  CMCubed = 0xa,
  CMCubedPerSec = 0xb,
  Degrees = 0xc,
}

/** Decoded RegionFlags bit 2 — DopplerScaleType. */
export enum DopplerScaleType {
  Velocity = 0,
  Frequency = 1,
}

/** Decoded RegionFlags bits 3-4 — scrolling region kind. */
export enum ScrollingRegionKind {
  Unspecified = 0,
  Scrolling = 1,
  Sweeping = 2,
  SweepingThenScrolling = 3,
}

export type PriorityLabel = 'High Priority' | 'Low Priority';
export type DopplerScaleTypeLabel = 'velocity' | 'frequency';
export type ScrollingRegionLabel =
  | 'unspecified'
  | 'scrolling'
  | 'sweeping'
  | 'sweepingThenScrolling';

/**
 * Backend-parsed RegionFlags (0018,6016). Matches the serializer in
 * `UltrasoundRegionSerializer` — lowercase string labels for categorical
 * fields plus the raw integer for debugging / re-parsing.
 */
export interface RegionFlags {
  priority: PriorityLabel;
  scalingProtected: boolean;
  dopplerScaleType: DopplerScaleTypeLabel;
  scrolling: ScrollingRegionLabel;
  raw: number;
}

const REGION_SPATIAL_FORMAT_LABELS: Record<number, string> = {
  [RegionSpatialFormat.NoneOrNotApplicable]: 'None or not applicable',
  [RegionSpatialFormat.TwoD]: '2D (tissue or flow)',
  [RegionSpatialFormat.MMode]: 'M-Mode (tissue or flow)',
  [RegionSpatialFormat.Spectral]: 'Spectral (CW or PW Doppler)',
  [RegionSpatialFormat.Waveform]: 'Waveform',
  [RegionSpatialFormat.Graphics]: 'Graphics',
};

const REGION_DATA_TYPE_LABELS: Record<number, string> = {
  [RegionDataType.NoneOrNotApplicable]: 'None or not applicable',
  [RegionDataType.Tissue]: 'Tissue',
  [RegionDataType.ColorFlow]: 'Color Flow',
  [RegionDataType.PWSpectralDoppler]: 'PW Spectral Doppler',
  [RegionDataType.CWSpectralDoppler]: 'CW Spectral Doppler',
  [RegionDataType.DopplerMeanTrace]: 'Doppler Mean Trace',
  [RegionDataType.DopplerModeTrace]: 'Doppler Mode Trace',
  [RegionDataType.DopplerMaxTrace]: 'Doppler Max Trace',
  [RegionDataType.VolumeTrace]: 'Volume Trace',
  [RegionDataType.ECGTrace]: 'ECG Trace',
  [RegionDataType.PulseTrace]: 'Pulse Trace',
  [RegionDataType.PhonocardiogramTrace]: 'Phonocardiogram Trace',
  [RegionDataType.GrayBar]: 'Gray bar',
  [RegionDataType.ColorBar]: 'Color bar',
  [RegionDataType.IntegratedBackscatter]: 'Integrated Backscatter',
  [RegionDataType.AreaTrace]: 'Area Trace',
  [RegionDataType.DAreaDt]: 'd(area)/dt',
  [RegionDataType.OtherPhysiological]:
    'Other Physiological (Amplitude vs. Time) input',
};

const PHYSICAL_UNITS_LABELS: Record<number, string> = {
  [PhysicalUnits.NoneOrNotApplicable]: 'None or not applicable',
  [PhysicalUnits.Percent]: 'Percent',
  [PhysicalUnits.DB]: 'dB',
  [PhysicalUnits.CM]: 'cm',
  [PhysicalUnits.Seconds]: 'seconds',
  [PhysicalUnits.Hertz]: 'hertz (seconds^-1)',
  [PhysicalUnits.DBPerSecond]: 'dB/seconds',
  [PhysicalUnits.CMPerSec]: 'cm/sec',
  [PhysicalUnits.CMSquared]: 'cm^2',
  [PhysicalUnits.CMSquaredPerSec]: 'cm^2/sec',
  [PhysicalUnits.CMCubed]: 'cm^3',
  [PhysicalUnits.CMCubedPerSec]: 'cm^3/sec',
  [PhysicalUnits.Degrees]: 'degrees',
};

function lookupLabel(
  table: Record<number, string>,
  code: number | null | undefined
): string | null {
  if (code === null || code === undefined) return null;
  const label = table[code];
  return label ?? null;
}

export function regionSpatialFormatLabel(
  code: number | null | undefined
): string | null {
  return lookupLabel(REGION_SPATIAL_FORMAT_LABELS, code);
}

export function regionDataTypeLabel(
  code: number | null | undefined
): string | null {
  return lookupLabel(REGION_DATA_TYPE_LABELS, code);
}

export function physicalUnitsLabel(
  code: number | null | undefined
): string | null {
  return lookupLabel(PHYSICAL_UNITS_LABELS, code);
}

export function isSpectralDoppler(code: number | null | undefined): boolean {
  return (
    code === RegionDataType.PWSpectralDoppler ||
    code === RegionDataType.CWSpectralDoppler
  );
}

export function isTissue(code: number | null | undefined): boolean {
  return code === RegionDataType.Tissue;
}

export function isColorFlow(code: number | null | undefined): boolean {
  return code === RegionDataType.ColorFlow;
}

const SCROLLING_LABELS: Record<number, ScrollingRegionLabel> = {
  [ScrollingRegionKind.Unspecified]: 'unspecified',
  [ScrollingRegionKind.Scrolling]: 'scrolling',
  [ScrollingRegionKind.Sweeping]: 'sweeping',
  [ScrollingRegionKind.SweepingThenScrolling]: 'sweepingThenScrolling',
};

/**
 * Decode the DICOM RegionFlags (0018,6016) bitfield.
 * Bit layout:
 *   bit 0      → priority (0=High Priority, 1=Low Priority)
 *   bit 1      → scalingProtected (0=false, 1=true)
 *   bit 2      → dopplerScaleType (0=Velocity, 1=Frequency)
 *   bits 3-4   → scrolling region kind
 *   bits 5-31  → reserved
 *
 * Note bit 0 is NOT inverted: 0 means "High Priority" per PS3.3 — AP-380
 * locked this down after the backend had it reversed.
 */
/* eslint-disable no-bitwise -- DICOM RegionFlags (0018,6016) is a bitfield; decoding it requires bitwise ops. */
export function parseRegionFlags(
  value: number | null | undefined
): RegionFlags | null {
  if (value === null || value === undefined) return null;
  if (!Number.isFinite(value)) return null;
  const raw = value | 0;

  const priority: PriorityLabel =
    (raw & 0b1) === 0 ? 'High Priority' : 'Low Priority';
  const scalingProtected = (raw & 0b10) !== 0;
  const dopplerScaleType: DopplerScaleTypeLabel =
    (raw & 0b100) === 0 ? 'velocity' : 'frequency';
  const scrollingBits = (raw >> 3) & 0b11;
  const scrolling = SCROLLING_LABELS[scrollingBits] ?? 'unspecified';

  return { priority, scalingProtected, dopplerScaleType, scrolling, raw };
}
/* eslint-enable no-bitwise */
